import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { loadProjectIntoStateAtom, resetSceneStateAtom, isProjectLoadingAtom, sceneListAtom } from '../atoms/sceneAtoms';
import { resetDadanExportStateAtom } from '../atoms/exportAtoms';
import { projectsManager } from '../services/projectsManager';
import { MultiSceneProject, Scene, ProjectStatus } from '../../../types/scene';
import logger from '../utils/logger';
import SceneService from '../services/sceneService';
import { detectPendingUploads } from '../services/uploadRecoveryService';
import { crudUtil } from '../services/axiosCRUD';
import { getAllPendingRecordings } from '../idb/chunk-db';

const SUSPECT_DURATION_MS = 60000;

export const useProjectLoader = () => {
  const [searchParams] = useSearchParams();
  const [, loadProjectIntoState] = useAtom(loadProjectIntoStateAtom);
  const [, resetSceneState] = useAtom(resetSceneStateAtom);
  const [, setIsProjectLoading] = useAtom(isProjectLoadingAtom);
  const [, setSceneList] = useAtom(sceneListAtom);
  const resetDadanExportState = useSetAtom(resetDadanExportStateAtom);
  const loadedProjectIdRef = useRef<number | null>(null);
  const fixedScenesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const projectIdParam = searchParams.get('projectId');
    const mode = searchParams.get('mode');
    
    // CRITICAL: Reset export state IMMEDIATELY on any project navigation
    // This prevents the export popover from flashing briefly when switching projects
    resetDadanExportState();
    
    // Handle blank mode: create a new blank project
    if (mode === 'blank' && !projectIdParam) {
      logger.info('[useProjectLoader] Blank mode detected, creating new blank project');
      
      const createBlankProject = async () => {
        setIsProjectLoading(true);
        try {
          // Note: resetDadanExportState already called above
          const blankProject = await SceneService.createProject({
            title: 'Quick Start',
            scenes: [],
            totalDuration: 0,
            status: 'draft' as const,
          });
          
          loadProjectIntoState(blankProject);
          loadedProjectIdRef.current = null;
          
          logger.info('[useProjectLoader] Blank project created:', blankProject.id);
        } catch (error) {
          logger.error('[useProjectLoader] Failed to create blank project:', error);
          resetSceneState();
        } finally {
          setIsProjectLoading(false);
        }
      };
      
      createBlankProject();
      return;
    }
    
    if (!projectIdParam) {
      logger.info('[useProjectLoader] No projectId in URL params');
      return;
    }

    const projectId = parseInt(projectIdParam, 10);
    
    if (isNaN(projectId)) {
      logger.warn('[useProjectLoader] Invalid projectId in URL:', projectIdParam);
      return;
    }

    if (loadedProjectIdRef.current === projectId) {
      logger.info('[useProjectLoader] Project already loaded:', projectId);
      return;
    }

    const loadProject = async () => {
      setIsProjectLoading(true);
      // Note: resetDadanExportState already called synchronously at top of useEffect
      try {
        logger.info('[useProjectLoader] Loading project from database:', projectId);
        
        // Phase 4.3: Detect pending uploads from IndexedDB (canvas recordings)
        const pendingUploads = await detectPendingUploads();
        
        if (pendingUploads.size > 0) {
          logger.info(`[useProjectLoader] 🔍 Found ${pendingUploads.size} pending upload(s) in IndexedDB`);
        }
        
        // Also check for pending separate stream recordings in IndexedDB
        // Note: We get all pending recordings and filter by scene ID after loading scenes
        // because projectId may not be saved with the pending recording
        const allPendingSeparateStreamRecordings = await getAllPendingRecordings();
        
        if (allPendingSeparateStreamRecordings.length > 0) {
          logger.info(`[useProjectLoader] 🎬 Found ${allPendingSeparateStreamRecordings.length} total pending separate stream recording(s) in IndexedDB`);
        }
        
        // Backend returns { project, scenes, transitions } in one call
        const response = await projectsManager.getProject(projectId);
        const { project, scenes, transitions } = response as any;

        // Build a Set of sceneIds that have pending separate stream recordings
        const projectSceneIds = new Set(scenes.map((s: any) => s.id.toString()));
        const pendingSeparateStreamSceneIds = new Set(
          allPendingSeparateStreamRecordings
            .filter(r => projectSceneIds.has(r.sceneId))
            .map(r => r.sceneId)
        );
        
        if (pendingSeparateStreamSceneIds.size > 0) {
          logger.info(`[useProjectLoader] 🎬 Matched ${pendingSeparateStreamSceneIds.size} pending separate stream recording(s) to this project:`, 
            Array.from(pendingSeparateStreamSceneIds));
        }
        
        const projectScenes: Scene[] = scenes.map((scene: any) => {
          // Phase 4.3: Check if this scene has pending chunks
          const sceneIdStr = scene.id.toString();
          const hasPendingUpload = pendingUploads.has(sceneIdStr);
          const hasPendingSeparateStream = pendingSeparateStreamSceneIds.has(sceneIdStr);
          
          // Determine status: Use database status first, then check for pending uploads
          let status: Scene['status'];
          if (scene.status === 'pending_confirmation' || scene.status === 'recorded') {
            // Respect database status for confirmed/recorded scenes
            status = scene.status;
          } else if (scene.videoUrl) {
            // Legacy: if scene has videoUrl but no explicit status, assume recorded
            status = 'recorded';
          } else if (hasPendingSeparateStream) {
            // Pending separate stream recording needs confirmation
            status = 'pending_confirmation';
            logger.info(`[useProjectLoader] 🎬 Scene ${scene.id} (${scene.title}) has pending separate stream recording - marked as 'pending_confirmation'`);
          } else if (hasPendingUpload) {
            status = 'pending_upload';
            logger.info(`[useProjectLoader] ⚡ Scene ${scene.id} (${scene.title}) has pending upload - marked as 'pending_upload'`);
          } else {
            status = scene.status || 'pending';
          }
          
          return {
            id: sceneIdStr,
            projectId: project.id.toString(),
            title: scene.title,
            notes: scene.notes || '',
            tags: scene.tags || [],
            order: scene.sceneOrder,
            status,
            duration: scene.duration || 0,
            recordedAt: scene.videoUrl || hasPendingUpload || hasPendingSeparateStream ? new Date(scene.updatedAt).getTime() : undefined,
            videoUrl: scene.videoUrl,
            thumbnailUrl: scene.thumbnailUrl,
            thumbnail: scene.thumbnailUrl || undefined,
            videoChunks: [],
            layout: scene.metadata?.layout || [],
            background: scene.metadata?.background || scene.metadata?.layout?.background || { type: 'solid' as const, colors: ['#000000'] },
            script: scene.metadata?.script || scene.script || '',
            layoutPreset: scene.metadata?.layoutPreset,
            transitionOut: scene.metadata?.transitionOut,
            metadata: scene.metadata,
            micEnabled: scene.micEnabled ?? false, // Include micEnabled from backend (needed for scene analysis)
          };
        });

        const multiSceneProject: MultiSceneProject = {
          id: project.id.toString(),
          title: project.name,
          templateId: project.templateId || undefined,
          scenes: projectScenes,
          totalDuration: project.totalDuration || 0,
          status: (projectScenes.every(s => s.status === 'recorded') ? 'completed' : 
                   projectScenes.some(s => s.status === 'recorded') ? 'in-progress' : 
                   'draft') as ProjectStatus,
          createdAt: new Date(project.createdAt).getTime(),
          updatedAt: new Date(project.updatedAt).getTime(),
          aspectRatio: project.aspectRatio,
        };

        loadProjectIntoState(multiSceneProject);
        loadedProjectIdRef.current = projectId;
        
        logger.info('[useProjectLoader] Project loaded successfully:', {
          projectId,
          name: project.name,
          scenesCount: projectScenes.length,
          transitionsCount: transitions.length,
        });
        
        // Fix durations for scenes with suspect 60000ms duration (WebM metadata issue)
        const scenesToFix = projectScenes.filter(scene => 
          scene.videoUrl && 
          scene.duration === SUSPECT_DURATION_MS &&
          !fixedScenesRef.current.has(scene.id)
        );
        
        if (scenesToFix.length > 0) {
          logger.info(`[useProjectLoader] 🔧 Found ${scenesToFix.length} scene(s) with suspect duration (${SUSPECT_DURATION_MS}ms), fixing...`);
          
          // Fix durations in background (don't block UI)
          for (const scene of scenesToFix) {
            fixedScenesRef.current.add(scene.id);
            
            crudUtil.post(`/api/scene/${scene.id}/fix-duration`, {})
              .then((response: any) => {
                if (response.durationMs && response.success) {
                  logger.info(`[useProjectLoader] ✅ Fixed scene ${scene.id} duration: ${response.durationMs}ms`);
                  
                  // Update local state with the fixed duration
                  setSceneList(prevScenes => 
                    prevScenes.map(s => 
                      s.id === scene.id 
                        ? { ...s, duration: response.durationMs }
                        : s
                    )
                  );
                }
              })
              .catch((error: any) => {
                logger.warn(`[useProjectLoader] Failed to fix scene ${scene.id} duration:`, error.message);
                fixedScenesRef.current.delete(scene.id); // Allow retry
              });
          }
        }
      } catch (error) {
        logger.error('[useProjectLoader] Failed to load project:', error);
      } finally {
        setIsProjectLoading(false);
      }
    };

    loadProject();
  }, [searchParams, loadProjectIntoState, resetDadanExportState, setSceneList]);

  return {
    projectId: searchParams.get('projectId'),
  };
};
